import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency, withDrPrefix } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays, Users2, Stethoscope, IndianRupee,
  CheckCircle2, TrendingUp, Activity, ClipboardList,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

function Stat({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent || "bg-navy/5"}`}>
          <Icon className="h-4 w-4 text-navy" />
        </div>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <p className="mt-2 text-2xl font-bold text-navy">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

// `embedded` = rendered inside the Reports tabs, which supply their own
// page heading, so the standalone header is dropped.
export default function DoctorAnalytics({ embedded = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/doctor-performance")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" /></div>;
  if (!data || !data.doctors?.length) return <p className="py-12 text-center text-muted-foreground">No doctor data available.</p>;

  const isMultiDoctor = data.doctors.length > 1;
  // The server decides — it omits the revenue fields entirely for doctors, so
  // this only controls layout, never access.
  const showRevenue = data.show_revenue !== false;

  // Single doctor view
  if (!isMultiDoctor) {
    const d = data.doctors[0];
    return (
      <div className="space-y-6">
        <div>
          {!embedded && <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Doctor Analytics</p>}
          <h2 className="font-display text-2xl font-semibold text-navy">{withDrPrefix(d.name)}</h2>
          {d.specialization && <p className="text-sm text-muted-foreground">{d.specialization}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={CalendarDays} label="Today's Appts" value={d.today_appointments} accent="bg-blue-50" />
          <Stat icon={CalendarDays} label="This Month" value={d.month_appointments} sub={`${d.month_consultations} consultations`} />
          <Stat icon={Users2} label="Total Patients" value={d.unique_patients} accent="bg-green-50" />
          <Stat icon={CheckCircle2} label="Completion Rate" value={`${d.completion_rate}%`} accent="bg-emerald-50" />
        </div>

        {/* Revenue is a management figure — the server omits it entirely for
            doctors, so `showRevenue` gates presentation only. */}
        <div className={`grid grid-cols-2 gap-3 ${showRevenue ? "sm:grid-cols-4" : "sm:grid-cols-2"}`}>
          <Stat icon={Stethoscope} label="Total Consultations" value={d.total_consultations} />
          <Stat icon={ClipboardList} label="Total Sessions" value={d.total_sessions} />
          {showRevenue && <Stat icon={IndianRupee} label="This Month Revenue" value={formatCurrency(d.month_revenue)} accent="bg-gold/10" />}
          {showRevenue && <Stat icon={TrendingUp} label="Total Revenue" value={formatCurrency(d.total_revenue)} accent="bg-gold/10" />}
        </div>
      </div>
    );
  }

  // Multi-doctor comparison view (admin)
  const chartData = data.doctors.map((d) => ({
    name: d.name.replace(/^Dr\.?\s*/i, ""),
    Appointments: d.total_appointments,
    Completed: d.completed_appointments,
    ...(showRevenue ? { Revenue: d.total_revenue } : {}),
  }));

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Doctor Analytics</p>
          <h2 className="font-display text-2xl font-semibold text-navy">Performance Overview</h2>
        </div>
      )}
      <p className="text-sm text-muted-foreground">Period: {data.period.month_start} to {data.period.today}</p>

      {/* Chart */}
      <div className="rounded-xl border border-navy/10 bg-white p-5 shadow-sm">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Appointments by Doctor</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="Appointments" fill="#1B3A5C" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Completed" fill="#C49A6C" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-cream/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Doctor</th>
              <th className="px-4 py-3">Patients</th>
              <th className="px-4 py-3">Appointments</th>
              <th className="px-4 py-3">Consultations</th>
              <th className="px-4 py-3">Sessions</th>
              <th className="px-4 py-3">Completion</th>
              {showRevenue && <th className="px-4 py-3">Month Rev</th>}
              {showRevenue && <th className="px-4 py-3">Total Rev</th>}
            </tr>
          </thead>
          <tbody>
            {data.doctors.map((d) => (
              <tr key={d.doctor_id} className="border-b last:border-0 hover:bg-nude/30">
                <td className="px-4 py-3">
                  <p className="font-semibold text-ink">{withDrPrefix(d.name)}</p>
                  <p className="text-[10px] text-muted-foreground">{d.specialization || "—"}</p>
                </td>
                <td className="px-4 py-3">{d.unique_patients}</td>
                <td className="px-4 py-3">
                  <span className="font-medium">{d.total_appointments}</span>
                  <span className="ml-1 text-xs text-muted-foreground">({d.today_appointments} today)</span>
                </td>
                <td className="px-4 py-3">{d.total_consultations}</td>
                <td className="px-4 py-3">{d.total_sessions}</td>
                <td className="px-4 py-3">
                  <span className={`font-medium ${d.completion_rate >= 80 ? "text-green-600" : d.completion_rate >= 50 ? "text-amber-600" : "text-red-600"}`}>
                    {d.completion_rate}%
                  </span>
                </td>
                {showRevenue && <td className="px-4 py-3 text-gold font-medium">{formatCurrency(d.month_revenue)}</td>}
                {showRevenue && <td className="px-4 py-3 font-medium">{formatCurrency(d.total_revenue)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
